import type { BrowserWindow, Experiments, ILogger } from '@wix/thunderbolt-symbols'
import { tabClasses } from '@wix/thunderbolt-commons'
import { hasBrowserZoom as hasInitialBrowserZoom, isMobileUserAgent } from './zoomUtils'

const KEYBOARD_NAVIGATION_ACCESSIBILITY_KEYS = ['Tab', 'ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft']
const BASIC_ACCESSIBILITY_KEYS = ['Tab']
const WINDOW_HEIGHT_DIFF_THRESHOLD = 130
const WINDOW_HEIGHT_DIFF_THRESHOLD_MIN = 50

const hasBrowserZoom = (window: Window): boolean => {
	const probablyHasBrowserZoom = window.outerWidth !== window.innerWidth
	// we have to do height check to detect devTools open and docked to the right | left, since it influences innerWidth
	const windowHeightDiff = window.outerHeight - window.innerHeight
	const heightZoomedIn = windowHeightDiff > WINDOW_HEIGHT_DIFF_THRESHOLD // ignore diff for browser action bar
	const heightZoomedOut = windowHeightDiff < WINDOW_HEIGHT_DIFF_THRESHOLD_MIN
	return probablyHasBrowserZoom && (heightZoomedIn || heightZoomedOut)
}

const getWindowWidthRatio = (window: Window): string => {
	return (
		window.outerWidth && window.innerWidth // protection for browser loads a site in non-active tab: TB-12074
			? window.outerWidth / window.innerWidth
			: 1
	).toFixed(2)
}

const setBrowserZoom = (window: Window): void => {
	const browserZoom = hasBrowserZoom(window) ? getWindowWidthRatio(window) : '1.00'
	window!.document.documentElement.style.setProperty('--browser-zoom', browserZoom)
}

export const accessibilityUtils = (window: BrowserWindow, experiments: Experiments, logger?: ILogger) => {
	const isRepeaterKeyboardNavigationAvailable = experiments['specs.thunderbolt.repeater_keyboard_navigation']

	const FOCUS_RING_KEYS = isRepeaterKeyboardNavigationAvailable
		? KEYBOARD_NAVIGATION_ACCESSIBILITY_KEYS
		: BASIC_ACCESSIBILITY_KEYS

	return {
		addFocusRingAndKeyboardTabbingOnClasses: (event: KeyboardEvent): void => {
			if (FOCUS_RING_KEYS.includes(event.key)) {
				// TODO: renderer check how to change it
				// TODO: Think of a better way to get the site container in page features
				const target: HTMLElement = window!.document.getElementById('SITE_CONTAINER')!
				target.classList.add(...tabClasses)
			}
		},
		removeKeyboardTabbingOnClass: (e: PointerEvent | MouseEvent): void => {
			// if pointerType is Unknown (meaning that the event was not generated by a mouse device, a pen/stylus device or a touch, such as a finger), do not remove class
			if ('pointerType' in e && e.pointerType === '') {
				return
			}

			const target: HTMLElement = window!.document.getElementById('SITE_CONTAINER')!
			target.classList.remove('keyboard-tabbing-on')
		},
		addBrowserZoomForSPX: (): void => {
			const fixBrowserZoomForSPX = experiments['specs.thunderbolt.browserZoomForSPX']
			if (fixBrowserZoomForSPX) {
				let lastRatio = getWindowWidthRatio(window!)
				if (lastRatio !== '1.00') {
					// set initial zoom
					setBrowserZoom(window!)
				}

				let resizeTimeout: NodeJS.Timeout

				function handleResize() {
					const currentRatio = getWindowWidthRatio(window!)

					if (currentRatio !== lastRatio) {
						lastRatio = currentRatio
						setBrowserZoom(window!)
					}
				}

				window!.addEventListener('resize', () => {
					clearTimeout(resizeTimeout)
					resizeTimeout = setTimeout(handleResize, 100)
				})
			}
		},
		addBiForBrowserZoom: (): void => {
			if (!logger) {
				return
			}

			const hasInitialZoom = hasInitialBrowserZoom(window)
			const isMobile = isMobileUserAgent(window)

			const sendBi = () => {
				const paramsOverrides = {
					hasInitialZoom: String(hasInitialZoom),
					zoomDuringSession: 'true',
				}
				logger.interactionStarted('browser-zoom-during-session', { paramsOverrides })
				logger.interactionEnded('browser-zoom-during-session', { paramsOverrides })
			}

			if (isMobile) {
				window!.visualViewport!.addEventListener('resize', sendBi, { once: true })
			} else {
				const dpr = window!.devicePixelRatio || 1
				const mediaQuery = window!.matchMedia(`(resolution: ${dpr}dppx)`)
				mediaQuery.addEventListener('change', sendBi, { once: true })
			}
		},
	}
}
